import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Shield, Eye, EyeOff, Database, Lock, CheckCircle, XCircle, Globe, FileText, Users, BadgeCheck } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Privacy() {
  return (
    <Layout>
      <Helmet>
        <title>Privacy Policy — How We Protect Your Data | Transparency Badge</title>
        <meta name="description" content="Our free website checker collects zero personal data. Badge applicants share contact info voluntarily. Read exactly what we collect, how we use it, and your rights." />
        <link rel="canonical" href="https://transparencybadge.com/privacy" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Privacy First"
            title="Privacy Policy"
            description="We are committed to full transparency about what data we collect, who we collect it from, and how we use it."
          />
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">

          {/* Two-tier summary */}
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            <div className="bg-safe-bg border border-safe/30 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-safe text-safe-foreground shrink-0">
                  <Eye className="h-4 w-4" />
                </div>
                <h3 className="font-bold text-foreground">Website Visitors</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Using our free website checker requires <span className="font-semibold text-foreground">zero personal data</span>. No account, no registration, no tracking. We only process the domain name you enter.
              </p>
            </div>
            <div className="bg-accent/50 border border-primary/20 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                  <BadgeCheck className="h-4 w-4" />
                </div>
                <h3 className="font-bold text-foreground">Badge Applicants</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Website owners applying for the Verified Badge Program <span className="font-semibold text-foreground">voluntarily provide</span> contact and business information required to process their application.
              </p>
            </div>
          </div>

          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <FileText className="h-6 w-6 text-primary" />
              Introduction
            </h2>
            <div className="bg-card rounded-xl border p-6 space-y-4">
              <p className="text-muted-foreground">
                This Privacy Policy describes how Universal Website Transparency Badge ("we," "us," or "our"), operated by Shaik Faizaan, Hyderabad, India, collects, uses, and protects information when you use our website and services at transparencybadge.com.
              </p>
              <p className="text-muted-foreground">
                Our platform serves two distinct groups of users with very different data practices — website visitors who use our free checker, and website owners who apply for our Verified Badge Program. This policy clearly explains both.
              </p>
              <p className="text-muted-foreground">
                By using our service, you acknowledge that you have read and understood this Privacy Policy.
              </p>
            </div>
          </div>

          {/* Part 1 — Visitors */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-safe text-safe-foreground shrink-0">
                <span className="text-sm font-bold">1</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground">Free Website Checker — Visitors</h2>
            </div>
            <div className="bg-card rounded-xl border p-6 space-y-4">
              <p className="text-muted-foreground font-medium">
                If you are using our free website checker tool only, we collect no personal data from you whatsoever.
              </p>
              <div className="space-y-3">
                {[
                  { title: "No personal data", desc: "We do not collect your name, email, IP address, phone number, or any personally identifiable information." },
                  { title: "No account required", desc: "The checker is fully anonymous. No registration or login is needed." },
                  { title: "No browsing history", desc: "We do not track which websites you visit or your navigation patterns." },
                  { title: "No cookies or tracking", desc: "We do not use tracking cookies, advertising pixels, or any cross-site tracking technology." },
                  { title: "Domain names only", desc: "We process only the domain name you enter to query public safety databases and return a result." },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <XCircle className="h-4 w-4 text-risk shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{item.title} — </span>
                      <span className="text-sm text-muted-foreground">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Part 2 — Badge Program */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shrink-0">
                <span className="text-sm font-bold">2</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground">Verified Badge Program — Website Owners</h2>
            </div>
            <div className="bg-card rounded-xl border p-6 space-y-6">
              <p className="text-muted-foreground">
                Website owners who voluntarily apply for our Verified Badge Program provide personal and business information as part of their application. This data is necessary to review their website and manage their badge subscription.
              </p>

              {/* What we collect */}
              <div>
                <h4 className="font-semibold text-foreground mb-3">What We Collect</h4>
                <div className="space-y-2">
                  {[
                    "Full name — to identify the applicant and communicate about their application",
                    "Email address — to send application status, approval, and subscription updates",
                    "Country — for legal compliance and administrative purposes",
                    "Website domain — to conduct our manual safety review",
                    "Website category and description — to apply appropriate review standards",
                    "Website age and policy information — to assess legitimacy during review",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* How we use it */}
              <div>
                <h4 className="font-semibold text-foreground mb-3">How We Use This Data</h4>
                <div className="space-y-2">
                  {[
                    "To review and verify the website submitted in the application",
                    "To communicate approval, rejection, or feedback on the application",
                    "To manage the badge subscription and billing relationship",
                    "To send badge activation, renewal, and expiry notifications",
                    "To maintain records of verified domains for our checker service",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Storage */}
              <div>
                <h4 className="font-semibold text-foreground mb-3">Storage & Security</h4>
                <p className="text-sm text-muted-foreground">
                  Badge applicant data is stored securely in our Cloudflare D1 database, protected by encryption at rest and in transit. Data is stored only as long as the badge subscription is active. You may request deletion of your data at any time by emailing us at{" "}
                  <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline">hello@transparencybadge.com</a>.
                </p>
              </div>

              {/* What we don't do with it */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">We Never:</h4>
                <div className="space-y-1.5">
                  {[
                    "Sell or rent your personal data to third parties",
                    "Share your data with advertisers or data brokers",
                    "Use your data for marketing beyond our own service communications",
                    "Store payment card details (handled entirely by our payment processor)",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <XCircle className="h-3.5 w-3.5 text-risk shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Data Handling */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Database className="h-6 w-6 text-primary" />
              Data Handling & Storage
            </h2>
            <div className="bg-card rounded-xl border p-6 space-y-5">
              <div>
                <h4 className="font-semibold text-foreground mb-2">Checker Result Caching</h4>
                <p className="text-sm text-muted-foreground">
                  Domain safety check results may be temporarily cached to improve performance. This cache contains only domain-level technical data — never personal information. Cache is automatically cleared after 24 hours.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Third-Party Services</h4>
                <p className="text-sm text-muted-foreground">
                  Our checker queries publicly available threat databases and domain registration services. These services receive only domain names — no personal data is transmitted. Payments for badge subscriptions are processed by our payment provider who operates under their own privacy policy. We receive only payment confirmation — never card details.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Data Retention</h4>
                <p className="text-sm text-muted-foreground">
                  Visitor data: not collected, so nothing is retained. Badge applicant data: retained for the duration of the active badge subscription plus 90 days. After cancellation and the retention period, personal data is deleted from our systems.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Infrastructure</h4>
                <p className="text-sm text-muted-foreground">
                  Our platform runs on Cloudflare's global network. All communications are encrypted using HTTPS/TLS. Database storage uses Cloudflare D1 with encryption at rest.
                </p>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Shield className="h-6 w-6 text-primary" />
              Your Rights
            </h2>
            <div className="bg-card rounded-xl border p-6 space-y-4">
              <p className="text-muted-foreground">
                If you are a Verified Badge Program applicant or subscriber, you have the following rights regarding your personal data:
              </p>
              <div className="space-y-3">
                {[
                  { right: "Right to Access", desc: "Request a copy of the personal data we hold about you." },
                  { right: "Right to Correction", desc: "Request correction of any inaccurate personal data." },
                  { right: "Right to Deletion", desc: "Request deletion of your personal data at any time by contacting us." },
                  { right: "Right to Withdraw Consent", desc: "Cancel your badge subscription at any time without penalty." },
                ].map((item) => (
                  <div key={item.right} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{item.right} — </span>
                      <span className="text-sm text-muted-foreground">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                To exercise any of these rights, email{" "}
                <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline">hello@transparencybadge.com</a>.
                We respond to all data requests within 5 business days.
              </p>
            </div>
          </div>

          {/* Children */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              Children's Privacy
            </h2>
            <div className="bg-card rounded-xl border p-6">
              <p className="text-muted-foreground">
                Our free website checker collects no personal data from anyone, including children. The Verified Badge Program is intended for website owners and businesses, not children. We do not knowingly collect personal information from anyone under 18 years of age through our badge program. If we become aware that a minor has submitted an application, we will delete their information promptly.
              </p>
            </div>
          </div>

          {/* GDPR */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Globe className="h-6 w-6 text-primary" />
              International Users & GDPR
            </h2>
            <div className="bg-card rounded-xl border p-6 space-y-4">
              <p className="text-muted-foreground">
                Our service is available to users worldwide. For visitors using our free checker, no personal data is processed so GDPR requirements do not apply.
              </p>
              <p className="text-muted-foreground">
                For Verified Badge Program applicants in the EU and UK, we process your personal data on the legal basis of <span className="font-medium text-foreground">contractual necessity</span> — your data is required to provide the service you applied for. You have full rights under GDPR including access, correction, deletion, and data portability. To exercise these rights, contact us at hello@transparencybadge.com.
              </p>
              <p className="text-muted-foreground">
                Our platform is operated from India. By submitting your data through our badge program, you consent to its processing in accordance with this policy.
              </p>
            </div>
          </div>

          {/* Security */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
              <Lock className="h-6 w-6 text-primary" />
              Security Measures
            </h2>
            <div className="bg-card rounded-xl border p-6">
              <p className="text-muted-foreground mb-4">
                We take appropriate technical and organizational measures to protect all data we handle:
              </p>
              <ul className="space-y-3">
                {[
                  "All connections use HTTPS/TLS encryption",
                  "Badge applicant data stored in encrypted Cloudflare D1 database",
                  "Payment processing handled entirely by PCI-DSS compliant payment providers",
                  "No personal data stored in server logs",
                  "Regular security reviews of our infrastructure",
                  "Rate limiting and abuse prevention on all API endpoints",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Changes */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Changes to This Policy</h2>
            <div className="bg-card rounded-xl border p-6 space-y-4">
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date below and notify active badge subscribers by email. Continued use of our service after changes constitutes acceptance of the updated policy.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6">Contact Us About Privacy</h2>
            <div className="bg-card rounded-xl border p-6">
              <p className="text-muted-foreground mb-4">
                For any privacy questions, data requests, or concerns, contact us at:
              </p>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Universal Website Transparency Badge</p>
                <p className="text-sm text-muted-foreground">Operated by Shaik Faizaan</p>
                <p className="text-sm text-muted-foreground">Hyderabad, Telangana, India</p>
                <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline text-sm">
                  hello@transparencybadge.com
                </a>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                We respond to all privacy inquiries within 5 business days.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 border-t">
        <div className="container max-w-3xl text-center">
          <p className="text-sm text-muted-foreground">Last updated: March 2026</p>
        </div>
      </section>
    </Layout>
  );
}
